import { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "../chart/chart.css";
import { comsumptionPeriodMonths } from "../../../services/consumptionPeriodMonth";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getMonthsBetween(startDate: Date, endDate: Date): string[] {
  const months: string[] = [];
  const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const last = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

  while (current <= last && months.length < 12) {
    const monthName = new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(current);
    months.push(capitalizeFirstLetter(monthName));
    current.setMonth(current.getMonth() + 1);
  }

  return months;
}

interface BarChartProps {
  startDate: Date;
  endDate: Date;
  token: string;
  empresaID: string;
}

export const BarChart = ({ startDate, endDate, token , empresaID }: BarChartProps) => {
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    async function fetchData() {
      const formatDate = (date: Date): string =>
        `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-01`;
      if( startDate && endDate && token && empresaID !='') {
        const response = await comsumptionPeriodMonths(
          token,
          empresaID,
          formatDate(startDate),
          formatDate(endDate)
        );

        if (response.success) {
          const labels = getMonthsBetween(startDate, endDate);
          const apiData = response.result.consumoPeriodo;

          const values = labels.map((label) => {
            const found = apiData.find((item: { mes: string; }) => capitalizeFirstLetter(item.mes.toLowerCase()) === label);
            return found ? found.consumo : 0;
          });

          setData({
            labels,
            datasets: [
              {
                label: "Consultas",
                data: values,
                backgroundColor: ["rgb(0, 182, 222)"],
                categoryPercentage: 1.5,
                barThickness: 60,
              },
            ],
          });
        }
      }
    }

    fetchData();
  }, [startDate, endDate, token, empresaID]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          font: { size: 14 },
        },
        grid: {
          display: true,
          drawBorder: false,
          color: "#ccc",
          drawOnChartArea: false,
        },
        offset: true,
      },
      y: {
        display: false,
        grid: {
          display: false,
          drawTicks: false,
        },
        ticks: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        font: {
          size: 18,
          weight: "bold" as const,
        },
        padding: {
          top: 5,
          bottom: 0,
        },
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        display: true,
        anchor: "center" as const,
        align: "center" as const,
        color: (context: any) => {
          const bg = context.dataset.backgroundColor[context.dataIndex];
          if (typeof bg === "string") {
            const [r, g, b] = bg.match(/\d+/g)!.map(Number);
            const brightness = (r * 299 + g * 587 + b * 114) / 1000;
            return brightness > 150 ? "#000000" : "#FFFFFF";
          }
          return "#FFFFFF";
        },
        font: {
          weight: "bold" as const,
          size: 13,
        },
        formatter: (value: number) => value.toString(),
      },
    },
  };

  return (
    <div style={{ marginTop: "20px" }}>
      <div className="customflexBar">
        <div style={{ height: "250px" }}>
          {data ? (
            <Bar className="barChartContainer" data={data} options={options as any} />
          ) : (
            <div style={{marginTop:'110px'}}>Carregando gráfico...</div>
          )}
        </div>
      </div>
      <span className="spanCustom">
        <div className="divCustomColor"></div>
        Consultas realizadas
      </span>
    </div>
  );
};

export default BarChart;
